export function formatXPF(amount: number): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
  return `${formatted} XPF`;
}

/**
 * Formate un montant dans la devise fournie par le client (XPF, EUR, USD...).
 * Les devises sans centimes (XPF, JPY, KRW...) sont affichées sans décimales.
 */
export function formatCurrency(amount: number, currency?: string | null): string {
  const code = (currency || 'XPF').toUpperCase();
  const zeroDecimal = ['XPF', 'JPY', 'KRW', 'VND', 'CLP'].includes(code);
  try {
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: zeroDecimal ? 0 : 2,
      maximumFractionDigits: zeroDecimal ? 0 : 2,
    }).format(amount);
    return `${formatted} ${code}`;
  } catch {
    return `${amount} ${code}`;
  }
}

export function maskIban(iban: string): string {
  const clean = iban.replace(/\s+/g, '');
  if (clean.length <= 8) return clean;
  const start = clean.slice(0, 4);
  const end = clean.slice(-4);
  return `${start} •••• •••• ${end}`;
}

/**
 * Masque partiellement un IBAN pour l'affichage côté client :
 * on conserve les deux premiers groupes (ex. FR76 1724) et
 * les deux derniers (ex. 4888 108), le reste est masqué par des •.
 */
export function maskIbanPartial(iban: string): string {
  const groups = iban.trim().split(/\s+/).filter(Boolean);
  if (groups.length <= 4) return iban.trim();
  const head = groups.slice(0, 2).join(' ');
  const tail = groups.slice(-2).join(' ');
  const hidden = Array.from({ length: groups.length - 4 }, () => '••••').join(' ');
  return `${head} ${hidden} ${tail}`;
}

function hashSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Génère un IBAN au format français de façon déterministe à partir d'une clé.
 * Utilisé comme valeur de repli lorsqu'un client n'a pas encore d'IBAN enregistré.
 */
export function generateIban(seed: string): string {
  const check = String((hashSeed(`${seed}-chk`) % 90) + 10);
  const group = (suffix: string) => String((hashSeed(`${seed}-${suffix}`) % 9000) + 1000);
  const tail = String((hashSeed(`${seed}-tail`) % 900) + 100);
  return `FR${check} ${group('a')} ${group('b')} ${group('c')} ${group('d')} ${group('e')} ${tail}`;
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatDateTimeFull(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function formatDate(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}